import React, { useState } from 'react';
import { Edit2, Trash2, Copy, ChevronDown, ChevronUp, MessageCircle, CheckCircle2, Circle, Lightbulb } from 'lucide-react';

const ListaActividades = ({ 
  actividades, 
  onEditar, 
  onEliminar, 
  onDuplicar, 
  onCambiarEstado,
  onAgregarSubtarea,
  onCompletarSubtarea,
  onAgregarComentario,
  meses 
}) => {
  const [expandidos, setExpandidos] = useState({});
  const [comentarioTemp, setComentarioTemp] = useState({});

  const toggleExpandir = (id) => {
    setExpandidos(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const obtenerColorEstado = (estado) => {
    const colores = {
      'sin iniciar': 'bg-red-100 text-red-800 border-red-300',
      'en proceso': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'finalizada': 'bg-green-100 text-green-800 border-green-300',
      'cancelada': 'bg-gray-100 text-gray-800 border-gray-300',
      'programada': 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colores[estado] || 'bg-gray-100 text-gray-800';
  };

  const obtenerIconoEstado = (estado) => {
    const iconos = {
      'sin iniciar': '❌',
      'en proceso': '🔄',
      'finalizada': '✅',
      'cancelada': '⛔',
      'programada': '⏳'
    };
    return iconos[estado] || '❓';
  };

  const calcularProgreso = (fechaInicio, fechaCierre) => {
    const inicio = new Date(fechaInicio);
    const cierre = new Date(fechaCierre);
    const ahora = new Date();

    const totalDias = (cierre - inicio) / (1000 * 60 * 60 * 24);
    const diasTranscurridos = (ahora - inicio) / (1000 * 60 * 60 * 24);

    if (diasTranscurridos <= 0) return 0;
    if (diasTranscurridos >= totalDias) return 100;
    return Math.round((diasTranscurridos / totalDias) * 100);
  };

  const calcularProgresoSubtareas = (subtareas) => {
    if (!subtareas || subtareas.length === 0) return 0;
    const completadas = subtareas.filter(s => s.completada).length;
    return Math.round((completadas / subtareas.length) * 100);
  };

  const agregarComentarioLocal = (id) => {
    if (comentarioTemp[id]?.trim()) {
      onAgregarComentario(id, comentarioTemp[id]);
      setComentarioTemp(prev => ({ ...prev, [id]: '' }));
    }
  };

  if (actividades.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {actividades.map(actividad => {
        const progreso = calcularProgreso(actividad.fechaInicio, actividad.fechaCierre);
        const progresoSubtareas = calcularProgresoSubtareas(actividad.subtareas);
        const isExpanded = expandidos[actividad.id];

        return (
          <div key={actividad.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition">
            {/* Header Principal */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500">
              <div className="flex justify-between items-start gap-4 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-gray-800">{actividad.nombre}</h3>
                    <button
                      onClick={() => toggleExpandir(actividad.id)}
                      className="text-gray-600 hover:text-gray-800 transition p-1"
                    >
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">{actividad.proposito}</p>
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                  {/* Badge Estado */}
                  <select
                    value={actividad.estado}
                    onChange={(e) => onCambiarEstado(actividad.id, e.target.value)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border-2 transition cursor-pointer ${obtenerColorEstado(actividad.estado)}`}
                  >
                    <option value="sin iniciar">❌ Sin iniciar</option>
                    <option value="en proceso">🔄 En proceso</option>
                    <option value="finalizada">✅ Finalizada</option>
                    <option value="cancelada">⛔ Cancelada</option>
                    <option value="programada">⏳ Programada</option>
                  </select>
                </div>
              </div>

              {/* Info Basica */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm mb-3">
                <div>
                  <span className="text-gray-600 font-semibold">Responsable:</span>
                  <p className="text-gray-800">{actividad.responsable}</p>
                </div>
                <div>
                  <span className="text-gray-600 font-semibold">Inicio:</span>
                  <p className="text-gray-800">{actividad.fechaInicio}</p>
                </div>
                <div>
                  <span className="text-gray-600 font-semibold">Cierre:</span>
                  <p className="text-gray-800">{actividad.fechaCierre}</p>
                </div>
                <div>
                  <span className="text-gray-600 font-semibold">Mes:</span>
                  <p className="text-gray-800">{meses[parseInt(actividad.fechaInicio?.split('-')[1] || '1') - 1]}</p>
                </div>
              </div>

              {/* Barra de Progreso */}
              {actividad.estado !== 'programada' && (
                <div className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-semibold text-gray-700">Progreso:</span>
                    <span className="text-gray-600">{progreso}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        progreso < 50 ? 'bg-red-500' :
                        progreso < 100 ? 'bg-yellow-500' :
                        'bg-green-500'
                      }`}
                      style={{ width: `${progreso}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Etiquetas */}
              {actividad.etiquetas && actividad.etiquetas.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {actividad.etiquetas.map(etiqueta => (
                    <span key={etiqueta} className="px-2 py-1 bg-green-600 text-white text-xs rounded-full">
                      {etiqueta}
                    </span>
                  ))}
                </div>
              )}

              {/* Botones de Acción */}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => onEditar(actividad)}
                  className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-sm"
                >
                  <Edit2 size={16} /> Editar
                </button>
                <button
                  onClick={() => onDuplicar(actividad)}
                  className="flex items-center gap-1 px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 transition text-sm"
                >
                  <Copy size={16} /> Duplicar
                </button>
                <button
                  onClick={() => onEliminar(actividad.id)}
                  className="flex items-center gap-1 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition text-sm"
                >
                  <Trash2 size={16} /> Eliminar
                </button>
              </div>
            </div>

            {/* Sección Expandible */}
            {isExpanded && (
              <div className="border-t border-gray-200 p-4 space-y-4">
                {/* Subtareas */}
                {actividad.subtareas && actividad.subtareas.length > 0 && (
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-bold text-gray-800">✓ Subtareas</h4>
                      <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded-full">
                        {progresoSubtareas}% completado
                      </span>
                    </div>
                    
                    {/* Barra de progreso subtareas */}
                    <div className="w-full bg-purple-200 rounded-full h-2 mb-3 overflow-hidden">
                      <div
                        className="h-full bg-purple-600 transition-all"
                        style={{ width: `${progresoSubtareas}%` }}
                      ></div>
                    </div>

                    <div className="space-y-2">
                      {actividad.subtareas.map(subtarea => (
                        <div key={subtarea.id} className="flex items-center gap-2 bg-white p-2 rounded border border-purple-200">
                          <button
                            onClick={() => onCompletarSubtarea(actividad.id, subtarea.id)}
                            className="text-purple-600 hover:text-purple-800 transition flex-shrink-0"
                          >
                            {subtarea.completada ? (
                              <CheckCircle2 size={20} />
                            ) : (
                              <Circle size={20} />
                            )}
                          </button>
                          <span className={`flex-1 ${subtarea.completada ? 'line-through text-gray-500' : 'text-gray-800'}`}>
                            {subtarea.texto}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Comentarios */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <MessageCircle size={18} />
                    Comentarios
                  </h4>

                  {/* Agregar comentario */}
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      placeholder="Agregar comentario..."
                      value={comentarioTemp[actividad.id] || ''}
                      onChange={(e) => setComentarioTemp(prev => ({
                        ...prev,
                        [actividad.id]: e.target.value
                      }))}
                      onKeyPress={(e) => e.key === 'Enter' && agregarComentarioLocal(actividad.id)}
                      className="flex-1 px-3 py-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => agregarComentarioLocal(actividad.id)}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                    >
                      Enviar
                    </button>
                  </div>

                  {/* Lista de comentarios */}
                  {actividad.comentarios && actividad.comentarios.length > 0 ? (
                    <div className="space-y-2">
                      {actividad.comentarios.map(comentario => (
                        <div key={comentario.id} className="bg-white p-2 rounded border border-blue-200">
                          <p className="text-sm text-gray-800">{comentario.texto}</p>
                          <p className="text-xs text-gray-500 mt-1">{comentario.fecha}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600 italic">Sin comentarios aún</p>
                  )}
                </div>

                {/* Info adicional */}
                {actividad.recurrencia && actividad.recurrencia !== 'ninguna' && (
                  <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                    <p className="text-sm text-orange-800">
                      <Lightbulb size={16} className="inline mr-2" />
                      <strong>Actividad Recurrente:</strong> {actividad.recurrencia}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ListaActividades;
